import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthProvider';
import { supabase } from '@/lib/supabase';
import { inputDollarsToCents } from '@/lib/formatters';
import { blankToNull } from '@/features/admin/model';
import { useProfiles } from '@/features/users/hooks';
import { buildSoftwareRows, type SeatProfile } from './model';
import type {
  Inserts,
  SoftwareBillingCycle,
  SoftwareCategory,
  SoftwareStatus,
  Tables,
} from '@/types/database';

export type SoftwareSubscription = Tables<'software_subscriptions'>;
export type SoftwareSeat = Tables<'software_seats'>;

export const softwareKeys = {
  all: ['software'] as const,
  subscriptions: () => [...softwareKeys.all, 'subscriptions'] as const,
  seats: () => [...softwareKeys.all, 'seats'] as const,
};

function requireOrg(organizationId: string | undefined): string {
  if (!organizationId) throw new Error('Not signed in.');
  return organizationId;
}

function parseSeatCount(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error('Seats must be a whole number above zero.');
  }
  return parsed;
}

export function useSoftwareSubscriptions() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: softwareKeys.subscriptions(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('software_subscriptions')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SoftwareSubscription[];
    },
  });
}

/** Every seat in the org. Small enough to load once and group client-side. */
export function useSoftwareSeats() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: softwareKeys.seats(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('software_seats')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SoftwareSeat[];
    },
  });
}

/**
 * Subscriptions joined to their seats and people. `assignable` is who can
 * be given a seat or made owner: active members, never external lenders.
 */
export function useSoftwareCatalog() {
  const subscriptions = useSoftwareSubscriptions();
  const seats = useSoftwareSeats();
  const people = useProfiles();

  const profilesById = useMemo(() => {
    const map = new Map<string, SeatProfile>();
    for (const p of people.data ?? []) map.set(p.id, p);
    return map;
  }, [people.data]);

  const assignable = useMemo<SeatProfile[]>(
    () =>
      (people.data ?? [])
        .filter((p) => p.is_active && p.role !== 'external_lender')
        .map((p) => ({
          id: p.id,
          full_name: p.full_name,
          email: p.email,
          avatar_url: p.avatar_url,
        })),
    [people.data],
  );

  const rows = useMemo(
    () => buildSoftwareRows(subscriptions.data ?? [], seats.data ?? [], profilesById),
    [subscriptions.data, seats.data, profilesById],
  );

  return {
    rows,
    profilesById,
    assignable,
    isLoading: subscriptions.isLoading || seats.isLoading,
    error: subscriptions.error ?? seats.error,
  };
}

export interface SoftwareInput {
  id?: string;
  name: string;
  vendor: string;
  category: SoftwareCategory;
  status: SoftwareStatus;
  planName: string;
  website: string;
  accountLogin: string;
  ownerId: string | null;
  billingCycle: SoftwareBillingCycle;
  price: string;
  pricePerSeat: boolean;
  seatCount: string;
  annualOptionAvailable: boolean | null;
  annualPrice: string;
  startedOn: string;
  renewsOn: string;
  autoRenews: boolean;
  paymentMethod: string;
  includes: string;
  notes: string;
}

/** Resolves to the saved row's id so a create can navigate to its page. */
export function useSaveSoftware() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: SoftwareInput): Promise<string> => {
      const organizationId = requireOrg(profile?.organization_id);
      const name = input.name.trim();
      if (!name) throw new Error('Name is required.');
      const fields = {
        name,
        vendor: blankToNull(input.vendor),
        category: input.category,
        status: input.status,
        plan_name: blankToNull(input.planName),
        website: blankToNull(input.website),
        account_login: blankToNull(input.accountLogin),
        owner_id: input.ownerId,
        billing_cycle: input.billingCycle,
        price_cents: inputDollarsToCents(input.price),
        price_per_seat: input.pricePerSeat,
        seat_count: parseSeatCount(input.seatCount),
        annual_option_available: input.annualOptionAvailable,
        annual_price_cents:
          input.annualOptionAvailable === true ? inputDollarsToCents(input.annualPrice) : null,
        started_on: blankToNull(input.startedOn),
        renews_on: blankToNull(input.renewsOn),
        auto_renews: input.autoRenews,
        payment_method: blankToNull(input.paymentMethod),
        includes: blankToNull(input.includes),
        notes: blankToNull(input.notes),
      };

      if (input.id) {
        const { error } = await supabase
          .from('software_subscriptions')
          .update(fields)
          .eq('id', input.id);
        if (error) throw error;
        return input.id;
      }

      const payload: Inserts<'software_subscriptions'> = {
        organization_id: organizationId,
        created_by: profile?.id ?? null,
        ...fields,
      };
      const { data, error } = await supabase
        .from('software_subscriptions')
        .insert(payload)
        .select('id')
        .single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: softwareKeys.subscriptions() });
    },
  });
}

export function useDeleteSoftware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('software_subscriptions').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: softwareKeys.all });
    },
  });
}

export interface SeatInput {
  id?: string;
  subscriptionId: string;
  profileId: string | null;
  assigneeName: string;
  login: string;
  licenseType: string;
  assignedOn: string;
  notes: string;
}

export function useSaveSoftwareSeat() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: SeatInput) => {
      const organizationId = requireOrg(profile?.organization_id);
      const assigneeName = input.profileId ? null : blankToNull(input.assigneeName);
      if (!input.profileId && !assigneeName) {
        throw new Error('Pick a person or enter a name for the seat.');
      }
      const fields = {
        profile_id: input.profileId,
        assignee_name: assigneeName,
        login: blankToNull(input.login),
        license_type: blankToNull(input.licenseType),
        assigned_on: blankToNull(input.assignedOn),
        notes: blankToNull(input.notes),
      };
      if (input.id) {
        const { error } = await supabase.from('software_seats').update(fields).eq('id', input.id);
        if (error) throw error;
        return;
      }
      const payload: Inserts<'software_seats'> = {
        organization_id: organizationId,
        subscription_id: input.subscriptionId,
        created_by: profile?.id ?? null,
        ...fields,
      };
      const { error } = await supabase.from('software_seats').insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: softwareKeys.seats() });
    },
  });
}

export function useDeleteSoftwareSeat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('software_seats').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: softwareKeys.seats() });
    },
  });
}
